import { useScienceTutorSection, tutorSelection } from './scienceTutorContext';
import { Info, Scale, Thermometer } from 'lucide-react';
import { useState } from 'react';

import { formatSig, formatSigned, type AnswerCheck } from './energyCalorimetry';
import {
  DEFAULT_TEMPERATURE_K,
  KELVIN_OFFSET,
  MAX_TEMPERATURE_K,
  MIN_TEMPERATURE_K,
  SIGN_CASE_LABELS,
  SPONTANEITY_BOUNDARIES,
  SPONTANEITY_CONDITIONS,
  SPONTANEITY_EXTENSION_NOTE,
  SPONTANEITY_REACTIONS,
  SPONTANEITY_SOURCES,
  VERDICT_LABELS,
  checkCrossoverAnswer,
  checkGibbsAnswer,
  checkSignCasePrediction,
  checkVerdictPrediction,
  crossoverTemperatureK,
  entropyTermKJ,
  gibbsFreeEnergy,
  gibbsLine,
  gibbsWorking,
  type SignCase,
  type SpontaneityReaction,
  type SpontaneityVerdict,
} from './energySpontaneity';
import { FeedbackCard } from './EnergyCalorimetryVisuals';
import type { ModelField } from './modelInputContract';
import { ModelInputPanel } from './ModelInputs';

const SIGN_CASE_OPTIONS: SignCase[] = ['always', 'low-temperature', 'high-temperature', 'never'];
const VERDICT_OPTIONS: SpontaneityVerdict[] = [
  'spontaneous',
  'non-spontaneous',
  'near-equilibrium',
];
const PRESET_TEMPERATURES_K = [273, 298, 373, 500, 1000, 1500];
const TEMPERATURE_STEP_K = 1;
const TEMPERATURE_FIELDS: ModelField<'temperatureK'>[] = [
  { key: 'temperatureK', label: 'Temperature', unit: 'K', min: MIN_TEMPERATURE_K, max: MAX_TEMPERATURE_K, step: TEMPERATURE_STEP_K },
];

const CHART_WIDTH = 420;
const CHART_HEIGHT = 240;
const CHART_LEFT = 56;
const CHART_RIGHT = 400;
const CHART_TOP = 20;
const CHART_BOTTOM = 200;

interface Checks {
  signCase: AnswerCheck;
  verdict: AnswerCheck;
  gibbs: AnswerCheck | null;
  crossover: AnswerCheck | null;
}

/** ΔG–T straight line drawn from ΔH and ΔS, with the zero line, crossover and chosen T. */
function GibbsChart({
  reaction,
  temperatureK,
}: {
  reaction: SpontaneityReaction;
  temperatureK: number;
}) {
  const points = gibbsLine(reaction);
  const values = [...points.map((point) => point.deltaGkJ), 0];
  const top = Math.max(...values);
  const bottom = Math.min(...values);
  const span = top - bottom || 1;
  const x = (t: number) =>
    CHART_LEFT +
    ((t - MIN_TEMPERATURE_K) / (MAX_TEMPERATURE_K - MIN_TEMPERATURE_K)) *
      (CHART_RIGHT - CHART_LEFT);
  const y = (g: number) => CHART_TOP + ((top - g) / span) * (CHART_BOTTOM - CHART_TOP);
  const crossover = crossoverTemperatureK(reaction);
  const crossoverOnChart =
    crossover !== null && crossover >= MIN_TEMPERATURE_K && crossover <= MAX_TEMPERATURE_K;
  const current = gibbsFreeEnergy(reaction, temperatureK);
  const label = `ΔG against temperature from ${MIN_TEMPERATURE_K} to ${MAX_TEMPERATURE_K} K: ΔG is ${formatSigned(points[0].deltaGkJ, 3)} kJ at ${MIN_TEMPERATURE_K} K and ${formatSigned(points[1].deltaGkJ, 3)} kJ at ${MAX_TEMPERATURE_K} K${crossover !== null ? `; crossover at ${formatSig(crossover, 3)} K` : '; no crossover'}`;

  return (
    <svg
      viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
      className="h-auto w-full"
      role="img"
      aria-label={label}
    >
      <rect
        x={CHART_LEFT}
        y={CHART_TOP}
        width={CHART_RIGHT - CHART_LEFT}
        height={Math.max(0, y(0) - CHART_TOP)}
        className="fill-wash-coral"
      />
      <rect
        x={CHART_LEFT}
        y={y(0)}
        width={CHART_RIGHT - CHART_LEFT}
        height={Math.max(0, CHART_BOTTOM - y(0))}
        className="fill-wash-mint"
      />
      <line
        x1={CHART_LEFT}
        y1={CHART_TOP}
        x2={CHART_LEFT}
        y2={CHART_BOTTOM}
        className="stroke-ink/40"
        strokeWidth="2"
      />
      <line
        x1={CHART_LEFT}
        y1={y(0)}
        x2={CHART_RIGHT}
        y2={y(0)}
        className="stroke-ink"
        strokeWidth="1.5"
        strokeDasharray="5 4"
      />
      <text x={CHART_LEFT + 4} y={CHART_TOP + 12} className="fill-slate2 text-[10px] font-bold">
        ΔG &gt; 0 · not spontaneous
      </text>
      <text x={CHART_LEFT + 4} y={CHART_BOTTOM - 6} className="fill-slate2 text-[10px] font-bold">
        ΔG &lt; 0 · spontaneous
      </text>
      <text x="4" y={y(0) + 4} className="fill-ink text-[10px] font-extrabold">
        ΔG = 0
      </text>
      <line
        x1={x(points[0].temperatureK)}
        y1={y(points[0].deltaGkJ)}
        x2={x(points[1].temperatureK)}
        y2={y(points[1].deltaGkJ)}
        className="stroke-brand-sky"
        strokeWidth="3"
      />
      {crossoverOnChart && crossover !== null ? (
        <g>
          <circle
            cx={x(crossover)}
            cy={y(0)}
            r="5"
            className="fill-brand-sunshine stroke-ink"
            strokeWidth="1.5"
          />
          <text
            x={x(crossover)}
            y={y(0) - 10}
            textAnchor="middle"
            className="fill-ink text-[10px] font-extrabold"
          >
            {formatSig(crossover, 3)} K
          </text>
        </g>
      ) : null}
      {current !== null ? (
        <g>
          <line
            x1={x(temperatureK)}
            y1={CHART_TOP}
            x2={x(temperatureK)}
            y2={CHART_BOTTOM}
            className="stroke-brand-coral"
            strokeWidth="1.5"
          />
          <rect
            x={x(temperatureK) - 4}
            y={y(current) - 4}
            width="8"
            height="8"
            className="fill-brand-coral"
          />
        </g>
      ) : null}
      <text x={CHART_LEFT} y={CHART_BOTTOM + 16} className="fill-slate2 text-[10px] font-bold">
        {MIN_TEMPERATURE_K} K
      </text>
      <text
        x={CHART_RIGHT}
        y={CHART_BOTTOM + 16}
        textAnchor="end"
        className="fill-slate2 text-[10px] font-bold"
      >
        {MAX_TEMPERATURE_K} K
      </text>
      <text
        x={(CHART_LEFT + CHART_RIGHT) / 2}
        y={CHART_BOTTOM + 32}
        textAnchor="middle"
        className="fill-slate2 text-[11px] font-bold"
      >
        Temperature, T (K) →
      </text>
    </svg>
  );
}

/** ΔH and −TΔS as two signed bars that add up to ΔG. */
function GibbsTerms({
  reaction,
  temperatureK,
}: {
  reaction: SpontaneityReaction;
  temperatureK: number;
}) {
  const entropyTerm = entropyTermKJ(reaction, temperatureK);
  const deltaG = reaction.deltaHkJ + entropyTerm;
  const rows = [
    { id: 'h', label: 'ΔH', value: reaction.deltaHkJ },
    { id: 'ts', label: '−TΔS', value: entropyTerm },
    { id: 'g', label: 'ΔG', value: deltaG },
  ];
  const maximum = Math.max(...rows.map((row) => Math.abs(row.value)), 1);
  return (
    <ul className="space-y-2" aria-label="Terms that make up ΔG">
      {rows.map((row) => {
        const width = (Math.abs(row.value) / maximum) * 50;
        const negative = row.value < 0;
        return (
          <li
            key={row.id}
            className="grid grid-cols-[3.5rem_minmax(0,1fr)_6.5rem] items-center gap-2 text-sm"
          >
            <span className="font-extrabold text-ink">{row.label}</span>
            <span className="relative h-4 rounded-full bg-white" aria-hidden="true">
              <span className="absolute inset-y-0 left-1/2 w-px bg-ink/40" />
              <span
                className={`absolute inset-y-0 rounded-full ${negative ? 'bg-brand-mint' : 'bg-brand-coral'}`}
                style={
                  negative
                    ? { right: '50%', width: `${width}%` }
                    : { left: '50%', width: `${width}%` }
                }
              />
            </span>
            <span className="text-right text-xs font-bold text-slate2">
              {formatSigned(row.value, 3)} kJ
            </span>
          </li>
        );
      })}
    </ul>
  );
}

export function SpontaneityPanel() {
  const [reaction, setReaction] = useState<SpontaneityReaction>(SPONTANEITY_REACTIONS[0]);
  const [temperatureK, setTemperatureK] = useState(DEFAULT_TEMPERATURE_K);
  const [signPrediction, setSignPrediction] = useState<SignCase | null>(null);
  const [verdictPrediction, setVerdictPrediction] = useState<SpontaneityVerdict | null>(null);
  const [gibbsInput, setGibbsInput] = useState('');
  const [crossoverInput, setCrossoverInput] = useState('');
  useScienceTutorSection('SpontaneityPanel', { reaction: tutorSelection(reaction), temperatureK, signPrediction, verdictPrediction, gibbsInput, crossoverInput });
  const [checks, setChecks] = useState<Checks | null>(null);
  const crossover = crossoverTemperatureK(reaction);

  const choose = (entry: SpontaneityReaction) => {
    setReaction(entry);
    setSignPrediction(null);
    setVerdictPrediction(null);
    setGibbsInput('');
    setCrossoverInput('');
    setChecks(null);
  };

  const changeTemperature = (next: number) => {
    setTemperatureK(next);
    setVerdictPrediction(null);
    setGibbsInput('');
    setChecks(null);
  };

  const check = () => {
    if (!signPrediction || !verdictPrediction) return;
    setChecks({
      signCase: checkSignCasePrediction(reaction, signPrediction),
      verdict: checkVerdictPrediction(reaction, temperatureK, verdictPrediction),
      gibbs: gibbsInput.trim() ? checkGibbsAnswer(reaction, temperatureK, gibbsInput) : null,
      crossover:
        crossover !== null && crossoverInput.trim()
          ? checkCrossoverAnswer(reaction, crossoverInput)
          : null,
    });
  };

  const optionClass = (active: boolean) =>
    `rounded-2xl border px-4 py-2 text-left text-sm font-extrabold ${active ? 'border-ink bg-ink text-white' : 'border-hairline bg-surface text-ink'}`;

  return (
    <div className="grid gap-6 xl:grid-cols-[300px_minmax(0,1fr)]">
      <fieldset className="rounded-[1.75rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft">
        <legend className="sr-only">Choose a reaction</legend>
        <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-sky">
          Reactions and changes
        </p>
        <div className="mt-3 grid gap-2">
          {SPONTANEITY_REACTIONS.map((entry) => (
            <button
              key={entry.id}
              type="button"
              aria-pressed={reaction.id === entry.id}
              onClick={() => choose(entry)}
              className={optionClass(reaction.id === entry.id)}
            >
              {entry.name}
            </button>
          ))}
        </div>
      </fieldset>

      <section className="min-w-0 rounded-[1.75rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6">
        <p className="flex items-start gap-2 rounded-2xl bg-wash-sky px-4 py-2 text-xs font-bold leading-5 text-ink-soft">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-brand-sky" aria-hidden="true" />
          {SPONTANEITY_EXTENSION_NOTE}
        </p>
        <h3 className="mt-4 text-xl font-extrabold text-ink">{reaction.name}</h3>
        <p className="mt-2 break-words font-mono text-sm font-bold text-ink">{reaction.equation}</p>
        <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
          <div className="rounded-2xl bg-surface p-3">
            <dt className="text-xs font-extrabold uppercase tracking-wide text-slate2">ΔH°</dt>
            <dd className="mt-1 font-extrabold text-ink">
              {formatSigned(reaction.deltaHkJ, 3)} kJ mol⁻¹
            </dd>
          </div>
          <div className="rounded-2xl bg-surface p-3">
            <dt className="text-xs font-extrabold uppercase tracking-wide text-slate2">ΔS°</dt>
            <dd className="mt-1 font-extrabold text-ink">
              {formatSigned(reaction.deltaSJPerK, 4)} J K⁻¹ mol⁻¹{' '}
              <span className="text-xs font-bold text-brand-coral">(joules, not kJ)</span>
            </dd>
          </div>
        </dl>
        <p className="mt-2 text-xs font-medium leading-5 text-slate2">
          Data: {reaction.conditions}.
        </p>

        <div className="mt-5 rounded-2xl border border-hairline bg-surface p-4">
          <p className="mb-3 flex items-center justify-between gap-3 text-sm font-extrabold text-ink">
            <span className="inline-flex items-center gap-2">
              <Thermometer className="h-4 w-4 text-brand-coral" aria-hidden="true" /> Temperature
            </span>
            <strong>
              {temperatureK} K ({formatSig(temperatureK - KELVIN_OFFSET, 3)} °C)
            </strong>
          </p>
          <ModelInputPanel
            legend="Choose the temperature"
            fields={TEMPERATURE_FIELDS}
            values={{ temperatureK }}
            onChange={(next) => changeTemperature(next.temperatureK)}
            sliderKey="temperatureK"
          />
          <div className="mt-3 flex flex-wrap gap-2" aria-label="Preset temperatures">
            {PRESET_TEMPERATURES_K.map((preset) => (
              <button
                key={preset}
                type="button"
                aria-pressed={temperatureK === preset}
                onClick={() => changeTemperature(preset)}
                className={`rounded-full border px-3 py-1 text-xs font-extrabold ${temperatureK === preset ? 'border-ink bg-ink text-white' : 'border-hairline bg-white text-ink'}`}
              >
                {preset} K
              </button>
            ))}
          </div>
        </div>

        <div className="mt-5 grid gap-5 lg:grid-cols-2">
          <fieldset>
            <legend className="text-sm font-extrabold text-ink">
              1. Predict from the signs of ΔH and ΔS
            </legend>
            <div className="mt-2 grid gap-2">
              {SIGN_CASE_OPTIONS.map((option) => (
                <button
                  key={option}
                  type="button"
                  aria-pressed={signPrediction === option}
                  onClick={() => {
                    setSignPrediction(option);
                    setChecks(null);
                  }}
                  className={optionClass(signPrediction === option)}
                >
                  {SIGN_CASE_LABELS[option]}
                </button>
              ))}
            </div>
          </fieldset>
          <fieldset>
            <legend className="text-sm font-extrabold text-ink">
              2. At {temperatureK} K, the forward reaction is…
            </legend>
            <div className="mt-2 grid gap-2">
              {VERDICT_OPTIONS.map((option) => (
                <button
                  key={option}
                  type="button"
                  aria-pressed={verdictPrediction === option}
                  onClick={() => {
                    setVerdictPrediction(option);
                    setChecks(null);
                  }}
                  className={optionClass(verdictPrediction === option)}
                >
                  {VERDICT_LABELS[option]}
                </button>
              ))}
            </div>
          </fieldset>
        </div>

        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <label className="text-sm font-extrabold text-ink">
            3. ΔG at {temperatureK} K (kJ mol⁻¹, optional)
            <input
              aria-label="Gibbs free energy change in kJ per mol"
              inputMode="decimal"
              value={gibbsInput}
              onChange={(event) => {
                setGibbsInput(event.target.value);
                setChecks(null);
              }}
              className="mt-2 w-full rounded-2xl border border-hairline bg-white px-4 py-3 font-medium text-ink outline-none focus:border-brand-sky"
              placeholder="ΔG = ΔH − TΔS"
            />
          </label>
          {crossover !== null ? (
            <label className="text-sm font-extrabold text-ink">
              4. Crossover temperature (K, optional)
              <input
                aria-label="Crossover temperature in kelvin"
                inputMode="decimal"
                value={crossoverInput}
                onChange={(event) => {
                  setCrossoverInput(event.target.value);
                  setChecks(null);
                }}
                className="mt-2 w-full rounded-2xl border border-hairline bg-white px-4 py-3 font-medium text-ink outline-none focus:border-brand-sky"
                placeholder="T = ΔH ÷ ΔS"
              />
            </label>
          ) : (
            <p className="self-end rounded-2xl bg-surface p-3 text-xs font-bold leading-5 text-slate2">
              4. Crossover temperature: decide from step 1 whether one exists.
            </p>
          )}
        </div>

        <button
          type="button"
          disabled={!signPrediction || !verdictPrediction}
          onClick={check}
          className="btn-pill-primary mt-5 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <Scale className="mr-2 inline h-4 w-4" aria-hidden="true" />
          Check spontaneity
        </button>

        {checks ? (
          <div className="mt-5 grid gap-3" aria-label="Spontaneity feedback">
            <FeedbackCard title="Sign case" check={checks.signCase} />
            <FeedbackCard title={`At ${temperatureK} K`} check={checks.verdict} />
            {checks.gibbs ? <FeedbackCard title="ΔG" check={checks.gibbs} /> : null}
            {checks.crossover ? <FeedbackCard title="Crossover" check={checks.crossover} /> : null}
            <div className="grid gap-4 rounded-2xl border border-hairline bg-surface p-4 lg:grid-cols-[minmax(0,1fr)_260px]">
              <GibbsChart reaction={reaction} temperatureK={temperatureK} />
              <div className="space-y-3">
                <GibbsTerms reaction={reaction} temperatureK={temperatureK} />
                <p className="text-xs font-bold leading-5 text-slate2">
                  {gibbsWorking(reaction, temperatureK)}
                </p>
                <p className="text-xs font-bold leading-5 text-slate2">
                  {crossover !== null
                    ? `Crossover: T = ΔH ÷ ΔS ≈ ${formatSig(crossover, 3)} K (${formatSig(crossover - KELVIN_OFFSET, 3)} °C).`
                    : 'No crossover: ΔH and −TΔS push ΔG the same way at every temperature.'}
                </p>
              </div>
            </div>
            <p className="text-sm font-medium leading-6 text-slate2">
              <strong className="text-ink">Model boundary:</strong> {reaction.boundary}
            </p>
          </div>
        ) : null}

        <ul className="mt-4 list-disc space-y-1 pl-5 text-xs font-medium leading-5 text-slate2">
          {SPONTANEITY_BOUNDARIES.map((boundary) => (
            <li key={boundary}>{boundary}</li>
          ))}
        </ul>
        <p className="mt-2 text-xs font-medium leading-5 text-slate2">
          {SPONTANEITY_CONDITIONS} Sources: {SPONTANEITY_SOURCES.nist.label};{' '}
          {SPONTANEITY_SOURCES.crc.label}. Values are pending teaching review.
        </p>
      </section>
    </div>
  );
}
